import { collection, doc, getDocs, addDoc, deleteDoc, updateDoc, query, orderBy, limit, serverTimestamp, where, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { getAuth } from 'firebase/auth';

const ANNOUNCEMENTS_COLLECTION = 'announcements';

// Get recent announcements
export const getRecentAnnouncements = async (limitCount = 10) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get recent announcements');
    }

    // Determine user type by checking which collection they exist in
    const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
    const isInternalUser = internalUserDoc.exists();

    const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
    const q = query(
      announcementsRef, 
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const announcements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));

    // Filter announcements based on user type and target audience
    const filteredAnnouncements = announcements.filter(announcement => {
      // Internal users see company announcements and general announcements
      if (isInternalUser) {
        return announcement.targetAudience === 'internal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      } else {
        // Portal users see client announcements and general announcements
        return announcement.targetAudience === 'portal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      }
    });

    return filteredAnnouncements;
  } catch (error) {
    console.error('Error getting recent announcements:', error);
    throw error;
  }
};

// Get announcements with seen status for current user
export const getAnnouncementsWithSeenStatus = async (limitCount = 20) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get announcements');
    }

    // Determine user type by checking which collection they exist in
    const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
    const isInternalUser = internalUserDoc.exists();

    const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
    const q = query(
      announcementsRef, 
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const announcements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));

    // Filter announcements based on user type and target audience
    const filteredAnnouncements = announcements.filter(announcement => {
      // Internal users see company announcements and general announcements
      if (isInternalUser) {
        return announcement.targetAudience === 'internal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      } else {
        // Portal users see client announcements and general announcements
        return announcement.targetAudience === 'portal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      }
    });

    // Check which announcements the current user has seen
    const seenAnnouncementsRef = collection(db, 'announcementSeen');
    const seenQuery = query(
      seenAnnouncementsRef,
      where('userId', '==', auth.currentUser.uid)
    );
    const seenSnapshot = await getDocs(seenQuery);
    const seenAnnouncementIds = seenSnapshot.docs.map(doc => doc.data().announcementId);

    // Add seen status to announcements and filter out seen ones
    return filteredAnnouncements
      .map(announcement => ({
        ...announcement,
        seen: seenAnnouncementIds.includes(announcement.id)
      }))
      .filter(announcement => !announcement.seen); // Only return unseen announcements
  } catch (error) {
    console.error('Error getting announcements with seen status:', error);
    throw error;
  }
};

// Create a new announcement
export const createAnnouncement = async (announcementData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to create announcements');
    }

    const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
    const newAnnouncement = {
      message: announcementData.message,
      createdBy: auth.currentUser.displayName || auth.currentUser.email,
      createdByUid: auth.currentUser.uid,
      createdAt: serverTimestamp(),
      // Add file information if provided
      fileUrl: announcementData.fileUrl || null,
      fileName: announcementData.fileName || null,
      fileSize: announcementData.fileSize || null,
      // Add announcement type and target audience
      type: announcementData.type || 'general',
      targetAudience: announcementData.type === 'company' ? 'internal' : announcementData.type === 'client' ? 'portal' : 'all',
      requiresAction: announcementData.requiresAction || false
    };
    
    const docRef = await addDoc(announcementsRef, newAnnouncement);
    
    console.log('✅ Announcement created successfully:', docRef.id);
    
    // Trigger new announcement notification
    try {
      const { triggerNewAnnouncementNotification } = await import('../notificationTriggerService');
      await triggerNewAnnouncementNotification({
        title: announcementData.message.substring(0, 50) + (announcementData.message.length > 50 ? '...' : ''),
        content: announcementData.message,
        author: auth.currentUser.displayName || auth.currentUser.email,
        type: announcementData.type || 'general',
        targetAudience: newAnnouncement.targetAudience,
        requiresAction: announcementData.requiresAction || false,
        timestamp: new Date().toISOString()
      });
      console.log('✅ New announcement notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending new announcement notification:', notificationError);
      // Don't fail the announcement creation if notification fails
    }
    
    return {
      id: docRef.id,
      ...newAnnouncement
    };
  } catch (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }
};

// Create an onboarding announcement
export const createOnboardingAnnouncement = async (userId) => {
  try {
    console.log('Creating onboarding announcement for userId:', userId);
    const auth = getAuth();
    console.log('Current auth user:', auth.currentUser);
    
    if (!auth.currentUser) {
      console.error('No authenticated user found');
      throw new Error('User must be authenticated to create onboarding announcements');
    }

    const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
    const onboardingAnnouncement = {
      message: "Welcome to Builders Breakthrough Advisors! 👋\n\nWe're excited you're considering partnering with us! The first step of this process will be to complete your business assessment. This will ensure we provide the best possible support for your goals.",
      createdBy: "Builders Breakthrough Advisors",
      createdByUid: "system",
      createdAt: serverTimestamp(),
      type: 'onboarding',
      requiresAction: true,
      targetUserId: userId,
      actionType: 'start_assessment'
    };
    
    console.log('Adding onboarding announcement to database:', onboardingAnnouncement);
    const docRef = await addDoc(announcementsRef, onboardingAnnouncement);
    
    console.log('✅ Onboarding announcement created successfully:', docRef.id);
    
    // Trigger new announcement notification for onboarding
    try {
      const { triggerNewAnnouncementNotification } = await import('../notificationTriggerService');
      await triggerNewAnnouncementNotification({
        title: 'Welcome to Builders Breakthrough Advisors!',
        content: onboardingAnnouncement.message,
        author: 'Builders Breakthrough Advisors',
        type: 'onboarding',
        targetAudience: 'portal',
        requiresAction: true,
        timestamp: new Date().toISOString()
      });
      console.log('✅ Onboarding announcement notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending onboarding announcement notification:', notificationError);
      // Don't fail the announcement creation if notification fails
    }
    
    const result = {
      id: docRef.id,
      ...onboardingAnnouncement
    };
    console.log('Onboarding announcement created successfully:', result);
    return result;
  } catch (error) {
    console.error('Error creating onboarding announcement:', error);
    throw error;
  }
};

// Delete an announcement (only by creator)
export const deleteAnnouncement = async (announcementId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete announcements');
    }

    const announcementRef = doc(db, ANNOUNCEMENTS_COLLECTION, announcementId);
    
    // Check if user is the creator of the announcement
    const announcementDoc = await getDocs(query(collection(db, ANNOUNCEMENTS_COLLECTION), where('__name__', '==', announcementId)));
    if (announcementDoc.empty) {
      throw new Error('Announcement not found');
    }
    
    const announcementData = announcementDoc.docs[0].data();
    if (announcementData.createdByUid !== auth.currentUser.uid) {
      throw new Error('Only the creator can delete this announcement');
    }

    await deleteDoc(announcementRef);
    return announcementId;
  } catch (error) {
    console.error('Error deleting announcement:', error);
    throw error;
  }
};

// Mark announcement as seen by current user
export const markAnnouncementAsSeen = async (announcementId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to mark announcements as seen');
    }

    const seenRef = collection(db, 'announcementSeen');
    const seenDoc = {
      userId: auth.currentUser.uid,
      announcementId: announcementId,
      seenAt: serverTimestamp()
    };
    
    await addDoc(seenRef, seenDoc);
    return true;
  } catch (error) {
    console.error('Error marking announcement as seen:', error);
    throw error;
  }
};

// Get count of unseen announcements for current user
export const getUnseenAnnouncementsCount = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get unseen announcements count');
    }

    // Determine user type by checking which collection they exist in
    const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
    const isInternalUser = internalUserDoc.exists();

    const announcementsRef = collection(db, ANNOUNCEMENTS_COLLECTION);
    const q = query(
      announcementsRef, 
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const announcements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));

    // Filter announcements based on user type and target audience
    const filteredAnnouncements = announcements.filter(announcement => {
      // Internal users see company announcements and general announcements
      if (isInternalUser) {
        return announcement.targetAudience === 'internal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      } else {
        // Portal users see client announcements and general announcements
        return announcement.targetAudience === 'portal' || 
               announcement.targetAudience === 'all' || 
               !announcement.targetAudience; // Backward compatibility
      }
    });

    // Check which announcements the current user has seen
    const seenAnnouncementsRef = collection(db, 'announcementSeen');
    const seenQuery = query(
      seenAnnouncementsRef,
      where('userId', '==', auth.currentUser.uid)
    );
    const seenSnapshot = await getDocs(seenQuery);
    const seenAnnouncementIds = seenSnapshot.docs.map(doc => doc.data().announcementId);

    // Count unseen announcements
    return filteredAnnouncements.filter(announcement => !seenAnnouncementIds.includes(announcement.id)).length;
  } catch (error) {
    console.error('Error getting unseen announcements count:', error);
    throw error;
  }
};

// Upload file for announcement
export const uploadAnnouncementFile = async (file) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload files');
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed. Please upload PDF, DOC, DOCX, XLS, XLSX, images, or text files.');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 10MB.');
    }

    // Create unique filename
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `announcements/${auth.currentUser.uid}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      fileUrl: downloadURL,
      fileName: file.name,
      fileSize: file.size
    };
  } catch (error) {
    console.error('Error uploading announcement file:', error);
    throw error;
  }
}; 